import { shallowRef } from 'vue'
import type { StationWeekList } from '@/Network/Types/Responses/StationResponses'
import type { LensColorBy } from '@/Network/Types/Requests/DashboardSavedViewRequests'
import { DoctorStationStatus } from '@/translations/doctor.station.status'

export type CardColors = { stripe: string; bg: string }

/** Vaga aberta: laranja em qualquer lente (spec §5.2). */
export const OPEN_STATION_COLORS: CardColors = { stripe: '#F18F1B', bg: '#FEF3E7' }

/** Paleta categórica acessível (sem laranja, reservado à vaga). Mesma dos mockups G6/L5. */
export const CATEGORICAL_PALETTE: readonly string[] = [
  '#7C3AED',
  '#1D4ED8',
  '#0F766E',
  '#C026D3',
  '#65A30D',
  '#9F1239',
  '#475569',
  '#0891B2',
  '#BE185D',
  '#0369A1'
]

export const NO_VALUE_COLOR = '#9CA3AF'

/** FNV-1a: estável entre sessões e navegadores. */
function hashKey(key: string): number {
  let hash = 2166136261
  for (let i = 0; i < key.length; i += 1) {
    hash ^= key.charCodeAt(i)
    hash = Math.imul(hash, 16777619)
  }
  return Math.abs(hash)
}

const hashIndex = (key: string): number => hashKey(key) % CATEGORICAL_PALETTE.length

/**
 * Cores distintas para as chaves que estão na tela: cada chave fica com a cor do hash quando
 * ela está livre (estável entre telas) e, se colidir, com a próxima cor livre da paleta. As
 * chaves são ordenadas antes, então o resultado não depende da ordem dos plantões. Com mais
 * chaves do que cores, as que sobram voltam para a cor do hash.
 */
export function assignCategoricalColors(keys: Iterable<string>): Map<string, string> {
  const size = CATEGORICAL_PALETTE.length
  const out = new Map<string, string>()
  const used = new Set<number>()
  const pending: string[] = []
  ;[...new Set(keys)].sort().forEach((key) => {
    const index = hashIndex(key)
    if (used.has(index)) {
      pending.push(key)
      return
    }
    used.add(index)
    out.set(key, CATEGORICAL_PALETTE[index])
  })
  pending.forEach((key) => {
    const start = hashIndex(key)
    let chosen = start
    for (let step = 1; step < size; step += 1) {
      const index = (start + step) % size
      if (!used.has(index)) {
        chosen = index
        break
      }
    }
    used.add(chosen)
    out.set(key, CATEGORICAL_PALETTE[chosen])
  })
  return out
}

// Atribuição da tela atual (setores e especialidades em grupos separados). Reativa: cards e
// legenda recalculam a cor quando a lista de plantões muda.
const screenColors = shallowRef<ReadonlyMap<string, string>>(new Map())

/** Cada grupo (ex.: setores, especialidades) disputa a paleta inteira de forma independente. */
export function setCategoricalColorGroups(...groups: Iterable<string>[]): void {
  const merged = new Map<string, string>()
  groups.forEach((group) => assignCategoricalColors(group).forEach((c, k) => merged.set(k, c)))
  screenColors.value = merged
}

export function categoricalColor(key: string): string {
  return screenColors.value.get(key) ?? CATEGORICAL_PALETTE[hashIndex(key)]
}

export function tint(hex: string, alpha: number): string {
  const n = parseInt(hex.slice(1), 16)
  return `rgba(${n >> 16},${(n >> 8) & 255},${n & 255},${alpha})`
}

export type CheckColorKey = 'ok' | 'ongoing' | 'late' | 'noout' | 'miss' | 'future'

/** `ongoing` = check-in feito e plantão em andamento (ainda sem check-out): não é "concluído". */
export const CHECK_COLORS: Record<CheckColorKey, { color: string; label: string }> = {
  ok: { color: '#198038', label: 'Check-in e check-out ok' },
  ongoing: { color: '#6FDC8C', label: 'Check-in ok (em andamento)' },
  late: { color: '#F1C21B', label: 'Com atraso ou saída antecipada' },
  noout: { color: '#3CA2B9', label: 'Sem check-out' },
  miss: { color: '#DA1E28', label: 'Check-in não realizado' },
  future: { color: '#C3C7D0', label: 'A realizar' }
}

export function checkColorKey(status?: DoctorStationStatus): CheckColorKey {
  switch (status) {
    case DoctorStationStatus.CHECKOUT_REALIZADO:
      return 'ok'
    case DoctorStationStatus.CHECKIN_REALIZADO:
      return 'ongoing'
    case DoctorStationStatus.CHECKIN_REALIZADO_COM_ATRASO:
    case DoctorStationStatus.CHECKOUT_REALIZADO_COM_ANTECEDENCIA:
      return 'late'
    case DoctorStationStatus.CHECKOUT_NAO_REALIZADO:
    case DoctorStationStatus.DISPONIBILIZADO_PARA_CHECKOUT:
      return 'noout'
    case DoctorStationStatus.CHECKIN_NAO_REALIZADO:
      return 'miss'
    default:
      return 'future'
  }
}

export const TYPE_COLORS: Record<'FIXO' | 'COBERTURA' | 'EXTRA', CardColors> = {
  FIXO: { stripe: '#84C8D7', bg: '#F7FCFD' },
  COBERTURA: { stripe: '#E37796', bg: '#FDF7F8' },
  EXTRA: { stripe: '#198038', bg: '#F6FDF9' }
}

/** Plantão de médico fora da equipe (`ourTeam = false`): cinza. */
export const OFF_TEAM_COLORS: CardColors = { stripe: '#BCBCCD', bg: '#F0F0F4' }

/** Cores de "Tipo" em hex, para os cards novos (Compacta/Mínima) que não usam as classes do withIcon. */
export function typeColors(card: StationWeekList): CardColors {
  if (card.opened) return OPEN_STATION_COLORS
  if (!card.ourTeam) return OFF_TEAM_COLORS
  const cell = card.cell === 'COBERTURA' || card.cell === 'EXTRA' ? card.cell : 'FIXO'
  return TYPE_COLORS[cell]
}

/** Alerta do ds-attendance: vermelho sólido, como o `withIcon` (texto branco por cima). */
export const ALERT_COLORS: CardColors = { stripe: '#DA1E28', bg: '#DA1E28' }

/**
 * Card em alerta? Mesma condição do `withIcon` (`flags.enabledDsAttendancePolicy && isAlert`);
 * vaga aberta não tem alerta (é o `vacancy`, não o `withIcon`).
 */
export const isCardAlert = (card: StationWeekList, alertEnabled: boolean): boolean =>
  alertEnabled && !card.opened && !!card.isAlert

/**
 * Cor dos cards novos (Compacta/Mínima): o alerta vermelho prevalece sobre a lente e o tipo,
 * como no `withIcon` do Confortável; depois a lente; por fim as cores de "Tipo".
 */
export function denseCardColors(
  card: StationWeekList,
  colorBy: LensColorBy,
  alertEnabled: boolean
): CardColors {
  if (isCardAlert(card, alertEnabled)) return ALERT_COLORS
  return resolveCardColors(card, colorBy) ?? typeColors(card)
}

const withTint = (color: string): CardColors => ({ stripe: color, bg: tint(color, 0.07) })

export const sectorColorKey = (card: StationWeekList): string | null =>
  card.sector ? `s:${card.sector.id}` : null

export const specialtyColorKey = (card: StationWeekList): string | null =>
  card.specialty ? `${card.specialty.source === 'official' ? 'o' : 'c'}:${card.specialty.id}` : null

/** `null` = manter a cor padrão do componente (lente "Tipo"). */
export function resolveCardColors(card: StationWeekList, colorBy: LensColorBy): CardColors | null {
  if (colorBy === 'tipo') return null
  if (card.opened) return OPEN_STATION_COLORS
  if (colorBy === 'setor') {
    const key = sectorColorKey(card)
    return withTint(key ? categoricalColor(key) : NO_VALUE_COLOR)
  }
  if (colorBy === 'especialidade') {
    const key = specialtyColorKey(card)
    return withTint(key ? categoricalColor(key) : NO_VALUE_COLOR)
  }
  return withTint(CHECK_COLORS[checkColorKey(card.status)].color)
}
